// eslint-disable-next-line max-lines-per-function
define( function run() {
	'use strict';

	/*
	 * ION UTILITY FUNCTIONS
	 * ===========================
	 *
	 * All functions up to date and tested as of v4.13 release
	 * Please quote version number when adding additional functions or updating existing ones
	 *
	 * Functions list:
	 * ---------------
	 * trigger - Trigger event natively
	 * addClass - add specified class to an element
	 * addMeasurements - get sum of measurements in an array
	 * convertToPx - add px suffix to a number
	 * debounce - prevent other functions from being called again too rapidly (eg on page resize)
	 * escapeHTML - safely escape any strings using built-in browser functionality
	 * extend - deep merge multiple objects together
	 * fade - vanilla JS replacement for jQuery's fadeIn / fadeOut functions
	 * find - find elements with a particular selector from within a given set of elements
	 * getAnchor - get anchor href
	 * getBreakpoint - gets viewport's current breakpoint based on standard ION widths
	 * getData - return an object containing a list of data attributes
	 * hexToRgba - Convert hex value to rgba
	 * htmlStringToElement - Convert an HTML string to a proper DOM element
	 * htmlStringToElements - Convert an HTML string to a node list of elements
	 * inRange - Test if two numbers are within a given range of each other
	 * isNode - Check if the the element passed in is an Element Node
	 * isOverflown - check if the content of an element has overflown its bounding box
	 * isVisible - check if an element is visible
	 * makeId - create a random ID
	 * matches - Find elements with a matching selector
	 * offset - find an element's position
	 * outerHeightTrue - get an element's total outer height (including margins etc)
	 * outerWidthTrue - get an element's total outer width (including margins etc)
	 * parents - return an array of an element's parents
	 * processInPageLinks - find all links with a specific selector and run specified function on them
	 * removeClass - remove specified class to an element
	 * removeMeasurementSuffix - remove measurement suffixes (px/em etc)
	 * resetStyles - Reset styles on an element
	 * requestLoadInto - load content from specified url/anchor into an element
	 * scrollParent - find an element's parent for scrolling purposes
	 * setAttributes - allow daisy chaining to set multiple attributes in one go
	 * siblings - find all sibling elements / sibling elements (with a particular selector if specified)
	 * slideToggle - vanilla JS replacement for jQuery's slideUp / slideDown / slideToggle functions
	 * subtractMeasurements - get remainder from subtracted measurements in an array
	 * unwrap - unwrap an element from its parent (removes parent)
	 * updateHistory - update browser history
	 * wrap - wrap element with a new one
	 * wrapInner - wrap the children of an element within a single new child element
	 */

	// Other functions
	return {
		smallTag : 'small',
		mediumTag : 'medium',
		largeTag : 'large',
		mediumBP : 45,
		largeBP : 67.5,
		baseFont : 16,
		urlRegExp : new RegExp( '#[a-zA-Z0-9-._~%!$&\'()*+,;=:@/?#]+' ),
		urlAnchorRegExp : new RegExp( '#[a-zA-Z]+[a-zA-Z0-9-._]*' ), // Only accept character valid in html4 for compatibility

		// eslint-disable-next-line complexity
		deepMerge : function( target, source ) {
			var instance = this,
				result = target;

			if ( !source && typeof source !== 'boolean' ) {
				return target; // Null, undefined values check
			}

			if ( Object.prototype.toString.call( source ) === '[object Array]' ) {
				if ( Object.prototype.toString.call( result ) !== '[object Array]' ) {
					result = [];
				}
				source.forEach( function loop( child, index ) {
					if ( result[ index ] ) {
						result[ index ] = instance.deepMerge( result[ index ], source[ index ] );
					} else {
						result[ index ] = instance.clone( source[ index ] );
					}
				});
			} else if ( typeof source === 'object' ) {
				// Testing that this is DOM
				if ( source.nodeType && typeof source.cloneNode === 'function' ) {
					result = source.cloneNode( true );
				} else if ( source instanceof RegExp ) {
					result = new RegExp( source );
				} else if ( target.prototype ) {
					result = source;
				} else if ( source instanceof Date ) {
					// Check that this is a literal
					result = new Date( source );
				} else {
					// It is an object literal
					if ( typeof result !== 'object' ) {
						result = {};
					}
					Object.keys( source ).forEach( function loop( key ) {
						if ( result[ key ] ) {
							result[ key ] = instance.deepMerge( result[ key ], source[ key ] );
						} else {
							result[ key ] = instance.clone( source[ key ] );
						}
					});
				}
			} else {
				result = source;
			}

			return result;
		},

		// eslint-disable-next-line complexity
		clone : function( item ) {
			var instance = this,
				types = [ Number, String, Boolean ],
				result;

			if ( !item && typeof item !== 'boolean' ) {
				return item; // Null, undefined values check
			}

			// Normalizing primitives if someone did new String('aaa'), or new Number('444');
			types.forEach( function loop( type ) {
				if ( item instanceof type ) {
					result = type( item );
				}
			});

			if ( typeof result === 'undefined' ) {
				if ( Object.prototype.toString.call( item ) === '[object Array]' ) {
					result = [];
					item.forEach( function loop( child, index ) {
						result[ index ] = instance.clone( child );
					});
				} else if ( typeof item === 'object' ) {
					// Testing that this is DOM
					if ( item.nodeType && typeof item.cloneNode === 'function' ) {
						result = item.cloneNode( true );
					} else if ( item instanceof RegExp ) {
						result = new RegExp( item );
					} else if ( item.prototype ) {
						result = item;
					} else if ( item instanceof Date ) {
						// Check that this is a literal
						result = new Date( item );
					} else {
						// It is an object literal
						result = {};
						Object.keys( item ).forEach( function loop( key ) {
							result[ key ] = instance.clone( item[ key ] );
						});
					}
				} else {
					result = item;
				}
			}

			return result;
		},

		// Function to help dispatch custom events
		trigger : function( element, eventName ) {
			var event = document.createEvent( 'Event' );

			event.initEvent( eventName, true, true );
			element.dispatchEvent( event );
		},

		// Add specified class to an element (v4.13 - Sep 19)
		addClass : function( element, classNames ) {
			var classes = classNames.split( ' ' );

			if ( element && element.classList ) {
				classes.forEach( function loop( className ) {
					if ( className ) {
						element.classList.add( className );
					}
				});
			}
		},

		// Get sum of measurements in an array (v4.13 - Sep 19)
		addMeasurements : function( measurementArray ) {
			var instance = this,
				measurement = 0,
				i;

			for ( i = 0; i < measurementArray.length; i++ ) {
				if ( typeof measurementArray[ i ] !== 'number' ) {
					measurementArray[ i ] = instance.removeMeasurementSuffix( measurementArray[ i ] ); // Value is NaN, so attempt to convert before continuing
				}
				if ( typeof measurementArray[ i ] === 'number' ) {
					measurement += measurementArray[ i ]; // Value is a number, so add it on to current total
				} else {
					measurement = 'auto'; // Value is set to auto (so don't change it), or something else entirely (so set to auto rather than failing completely)...
					i = measurementArray.length; // ... then set count to be complete so no more additions are run
				}
			}

			return measurement;
		},

		// Add px suffix to a number (v4.13 - Sep 19)
		convertToPx : function( number, noAuto ) {
			var newNumber;

			if ( number === 0 || number === '0' || ( number === 'auto' && noAuto ) ) { // Starting value is 0, or number being forced instead of auto
				newNumber = 0;
			} else if ( number === 'auto' || typeof number !== 'number' || Number.isNaN( number ) ) { // Starting value is auto or not a number
				newNumber = 'auto';
			} else { // Standard functionality
				newNumber = number + 'px';
			}
			return newNumber;
		},

		// Help to prevent other functions from being called again too rapidly (eg on page resize) (v4.13 - Sep 19)
		debounce : function( func, wait, immediate ) {
			var timeout;

			return function debounceFn() {
				var context = this,
					args = arguments,
					later = function() {
						timeout = null;
						if ( !immediate ) {
							func.apply( context, args );
						}
					},
					callNow = immediate && !timeout;

				clearTimeout( timeout );

				timeout = setTimeout( later, wait );
				if ( callNow ) {
					func.apply( context, args );
				}
			};
		},

		// Safely escape any strings using built-in browser functionality (v4.13 - Sep 19)
		escapeHtml : function( str ) {
			var div = document.createElement( 'div' );

			div.appendChild( document.createTextNode( str ) );
			return div.innerHTML;
		},

		// Deep merge objects together (v5.0.0 - Jan 20)
		extend : function() {
			var instance = this,
				result,
				i;

			if ( arguments.length > 0 ) {
				result = {};
				for ( i = 0; i < arguments.length; i++ ) {
					if ( arguments[ i ] ) {
						result = instance.deepMerge( result, arguments[ i ] );
					}
				}
			}

			return result;
		},

		/*
		 * Vanilla JS replacement for jQuery's fadeIn / fadeOut functions (v4.13 - Sep 19)
		 * eg: element.fade( target element, animation duration (in ms), { options } );
		 */
		fade : function( element, duration, options ) {
			var instance = this,
				fadeOptions = {
					variant : 'toggle', // ...in/out/toggle - default toggle
					displayNone : true, // ...boolean - default true
					minFade : 0, // ...number - default 0
					maxFade : 1, // ...number - default 1
					callback : function() {
						// Do nothing
					},
				};

			// Only run if not currently animating
			if ( instance.isNode( element ) && !element.classList.contains( 'is-animating' ) ) {
				// If options have been set update the default ones
				fadeOptions = instance.extend({}, fadeOptions, options );

				fadeOptions.transition = element.style.transition; // Save any current inline styles
				fadeOptions.webkitTransition = element.style.webkitTransition; // Save any current inline styles

				element.classList.add( 'is-animating' );

				if ( fadeOptions.variant === 'in' ) {
					instance.fadeIn( element, duration, fadeOptions );
				} else if ( fadeOptions.variant === 'out' ) {
					instance.fadeOut( element, duration, fadeOptions );
				} else if ( fadeOptions.variant === 'toggle' && ( element.style.opacity === 0 || element.style.display === 'none' ) ) {
					instance.fadeIn( element, duration, fadeOptions );
				} else {
					instance.fadeOut( element, duration, fadeOptions );
				}
			}
		},

		// Fade in - fade variant only
		fadeIn : function( element, duration, fadeOptions ) {
			var instance = this,
				timing = ( duration / 1000 ) + 's';

			if ( instance.isNode( element ) ) {
				element.style.transition = 'opacity ' + timing;
				element.style.webkitTransition = 'opacity ' + timing;
				element.style.opacity = fadeOptions.minFade;

				if ( fadeOptions.displayNone ) {
					element.style.display = 'block';
				}

				setTimeout( function timeoutFn() {
					element.style.opacity = fadeOptions.maxFade;

					setTimeout( function innerTimeoutFn() {
						element.classList.remove( 'is-animating' );
						if ( element.style.transition === null ) {
							element.style.removeProperty( 'transition' );
						} else {
							element.style.transition = fadeOptions.transition;
						}
						if ( element.style.webkitTransition === null ) {
							element.style.removeProperty( 'webkitTransition' );
						} else {
							element.style.webkitTransition = fadeOptions.webkitTransition;
						}
						element.style.webkitTransition = fadeOptions.webkitTransition;
						if ( fadeOptions.displayNone ) {
							element.style.display = 'block';
						}
						element.style.opacity = fadeOptions.maxFade;
						element.setAttribute( 'aria-hidden', 'false' );
						fadeOptions.callback();
					}, duration );
				}, 20 );
			}
		},

		// Fade out - fade variant only
		fadeOut : function( element, duration, fadeOptions ) {
			var instance = this,
				timing = ( duration / 1000 ) + 's';

			if ( instance.isNode( element ) ) {
				element.style.transition = 'opacity ' + timing;
				element.style.webkitTransition = 'opacity ' + timing;
				element.style.opacity = fadeOptions.maxFade;

				setTimeout( function timeoutFn() {
					element.style.opacity = fadeOptions.minFade;
					setTimeout( function innerTimeoutFn() {
						element.classList.remove( 'is-animating' );
						if ( element.style.transition === null ) {
							element.style.removeProperty( 'transition' );
						} else {
							element.style.transition = fadeOptions.transition;
						}
						if ( element.style.webkitTransition === null ) {
							element.style.removeProperty( 'webkitTransition' );
						} else {
							element.style.webkitTransition = fadeOptions.webkitTransition;
						}
						element.style.display = 'block';
						element.style.opacity = fadeOptions.minFade;

						if ( fadeOptions.displayNone && fadeOptions.minFade === 0 ) {
							// MinFade = 0 required as otherwise minFade of greater than 0 will get blatted to hidden anyway
							element.style.display = 'none';
							element.style.removeProperty( 'opacity' );
							element.setAttribute( 'aria-hidden', 'true' );
						}
						fadeOptions.callback();
					}, duration );
				}, 20 );
			}
		},

		// Find elements with a particular selector from within a given set of elements (v4.13 - Sep 19)
		find : function( elements, selector ) {
			var results = [],
				i,
				search;

			for ( i = 0; i < elements.length; i++ ) {
				search = elements[ i ].querySelectorAll( selector );
				if ( search !== null ) {
					results = results.concat( Array.prototype.slice.call( search ) );
				}
			}

			if ( results.length === 0 ) {
				results = null;
			}
			return results;
		},

		// Get anchor href (v4.13 - Sep 19)
		getAnchor : function() {
			var instance = this,
				currentUrl = window.location.href,
				fullHash = currentUrl.match( instance.urlRegExp ),
				partHash = currentUrl.match( instance.urlAnchorRegExp ),
				href = '';

			if ( !fullHash ) {
				// Do nothing
			} else if ( partHash !== null && fullHash.toString().localeCompare( partHash.toString() ) === 0 ) {
				href = partHash;
			} else {
				href = null;
			}

			return href;
		},

		// Get the current main breakpoint of the page so that we can adjust a component's behaviour (v4.13 - Sep 19)
		getBreakpoint : function( element ) {
			var instance = this,
				thisElement = window.document.body,
				tag = instance.smallTag,
				fontSize = instance.baseFont,
				largeBreakpoint = instance.largeBP + 'em',
				mediumBreakpoint = instance.mediumBP + 'em';

			if ( FRAMEWORK.standalone && typeof element !== 'undefined' ) {
				// Get the font size for the element that was passed in. Used for Standalone global nav
				thisElement = element;
				fontSize = instance.removeMeasurementSuffix( window.getComputedStyle( thisElement ).fontSize );
				largeBreakpoint = ( instance.largeBP * fontSize ) + 'px';
				mediumBreakpoint = ( instance.mediumBP * fontSize ) + 'px';
			}

			if ( window.matchMedia( '(min-width: ' + largeBreakpoint + ')' ).matches ) {
				tag = instance.largeTag;
			} else if ( window.matchMedia( '(min-width: ' + mediumBreakpoint + ')' ).matches ) {
				tag = instance.mediumTag;
			} else {
				// Do nothing
			}

			return tag;
		},

		// Return an object containing a list of data attributes (v4.13 - Sep 19)
		getData : function( element ) {
			var data = {},
				i,
				attrib,
				attribName;

			for ( i = 0; i < element.attributes.length; i++ ) {
				attrib = element.attributes[ i ];
				if ( attrib.specified && ( /^data-/ ).test( attrib.name ) ) {
					attribName = String( attrib.name );
					attribName = attribName.replace( /^data-/, '' );
					attribName = attribName.replace( /-\b\w/g, function fn( l ) {
						return l.toUpperCase();
					});
					attribName = attribName.replace( /-/g, '' );
					try {
						data[ attribName ] = JSON.parse( attrib.value );
					} catch ( error ) {
						data[ attribName ] = attrib.value;
					}
				}
			}
			return data;
		},

		// Convert hex value to rgba (v5.0 - May 20)
		hexToRgba : function( hex ) {
			var c;
			if ( ( /^#([A-Fa-f0-9]{3}){1,2}$/ ).test( hex ) ) {
				c = hex.substring( 1 ).split( '' );
				if ( c.length === 3 ) {
					// 6 digit shortcode
					c = [ c[ 0 ], c[ 0 ], c[ 1 ], c[ 1 ], c[ 2 ], c[ 2 ] ];
				}
				c = '0x' + c.join( '' );
				// eslint-disable-next-line no-bitwise
				return 'rgba( ' + [ ( c >> 16 ) & 255, ( c >> 8 ) & 255, c & 255 ].join( ', ' ) + ', 1)';
			}
			// If process fails, return original value
			return hex;
		},

		// Convert an HTML string to a proper DOM element (v4.13 - Sep 19)
		htmlStringToElement : function( str ) {
			var template = document.createElement( 'div' ),
				html = str.trim(); // Never return a text node of whitespace as the result

			template.innerHTML = html;
			return template.firstChild;
		},

		// Convert an HTML string to a node list of elements (v4.13 - Sep 19)
		htmlStringToElements : function( str ) {
			// Returns a node list rather than an array; may want to consider changing final line return to Array.from( template.childNodes );
			var template = document.createElement( 'div' ),
				html = str.trim(); // Never return a text node of whitespace as the result

			template.innerHTML = html;
			return template.childNodes; // Returns node list
		},

		inRange : function( originalNumber, comparator, range, inclusive ) {
			var min = comparator - range,
				max = comparator + range;

			return inclusive ? originalNumber >= min && originalNumber <= max : originalNumber > min && originalNumber < max;
		},

		// Check if the the element passed in is an Element Node (v4.15 - Nov 19)
		isNode : function( element ) {
			var exists = false;

			if ( element && element !== null && element.nodeType === Node.ELEMENT_NODE ) {
				exists = true;
			}
			return exists;
		},

		// Check if the content of an element has overflown its bounding box (v4.13 - Sep 19)
		isOverflown : function( element, axis ) {
			var hasOverflown = false;

			if ( axis === 'y' ) {
				hasOverflown = element.scrollHeight > element.clientHeight + 1;
			} else if ( axis === 'x' ) {
				hasOverflown = element.scrollWidth > element.clientWidth + 1;
			} else {
				hasOverflown = element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1;
			}
			return hasOverflown;
		},

		// Check if an element is visible (v4.13 - Sep 19)
		isVisible : function( element ) {
			var testHidden = ( element.offsetWidth === 0 && element.offsetHeight === 0 ) || window.getComputedStyle( element ).display === 'none';
			return !testHidden;
		},

		// Create a random ID (copied from utility.js) (v4.13 - Sep 19)
		makeId : function( prefix ) {
			var newID = '',
				poss = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
				possLength = poss.length,
				i;

			for ( i = 0; i < 5; i++ ) {
				newID += poss.charAt( Math.floor( Math.random() * possLength ) );
			}

			return ( prefix ? prefix : '' ) + newID;
		},

		// Find elements with a matching selector (v4.13 - Sep 19)
		matches : function( element, selector ) {
			var instance = this,
				result = false;

			if ( instance.isNode( element ) ) {
				// eslint-disable-next-line max-len
				result = ( element.matches || element.matchesSelector || element.msMatchesSelector || element.mozMatchesSelector || element.webkitMatchesSelector || element.oMatchesSelector ).call( element, selector );
			}
			return result;
		},

		// Find an element's position (v4.13 - Sep 19)
		offset : function( element ) {
			var rect = element.getBoundingClientRect();

			return {
				top : rect.top + window.pageYOffset,
				left : rect.left + window.pageXOffset,
			};
		},

		// Get an element's total outer height (including margins etc) (v4.13 - Sep 19)
		outerHeightTrue : function( element ) {
			var height = element.offsetHeight,
				style = getComputedStyle( element );

			height += Number.parseInt( style.marginTop, 10 ) + Number.parseInt( style.marginBottom, 10 );
			return height;
		},

		// Get an element's total outer width (including margins etc) (v4.13 - Sep 19)
		outerWidthTrue : function( element ) {
			var width = element.offsetWidth,
				style = getComputedStyle( element );

			width += Number.parseInt( style.marginLeft, 10 ) + Number.parseInt( style.marginRight, 10 );
			return width;
		},

		// Return an array of an element's parents (v4.13 - Sep 19)
		parents : function( element, customSelector ) {
			var elements = [],
				selector = customSelector || '*',
				currentEl = element.parentNode;

			while ( currentEl.parentNode ) {
				if ( currentEl.matches( selector ) ) {
					elements.push( currentEl );
				}
				currentEl = currentEl.parentNode;
			}
			return elements;
		},

		// Find all links with a specific selector and run specified function on them (v4.13 - Sep 19)
		processInPageLinks : function( selector, linkFunction ) {
			document.querySelectorAll( '[href*="#"]' + selector ).forEach( function loop( element ) {
				if ( window.location.pathname === element.pathname && window.location.host === element.host ) {
					linkFunction( element );
				}
			});
		},

		// Remove specified class to an element (v4.13 - Sep 19)
		removeClass : function( element, classNames ) {
			var classes;

			if ( element && element.classList ) {
				classes = classNames.split( ' ' );
				classes.forEach( function loop( className ) {
					if ( className ) {
						element.classList.remove( className );
					}
				});
			}
		},

		// Remove measurement suffixes (v4.13 - Sep 19)
		removeMeasurementSuffix : function( value, noAuto ) {
			var newValue = value;

			if ( typeof newValue === 'string' ) {
				if ( value === 'auto' && !noAuto ) {
					newValue = 'auto'; // Starting value is auto, number not being forced
				} else {
					newValue = newValue.replace( /q|mm|cm|in|pt|pc|px|em|ex|ch|rem|vw|vh|deg|%/g, '' ); // Remove valid css measurements such as px, em etc
					newValue = Number.parseFloat( newValue, 10 );
					if ( Number.isNaN( newValue ) ) { // New value is NaN despite suffix removal
						newValue = 0;
					}
				}
			} else if ( typeof newValue === 'number' ) {
				newValue = value; // Do nothing - starting value is already a number
			} else {
				newValue = 0; // Any other random case
			}
			return newValue;
		},

		// Reset styles on an element (v4.15 - Oct 19)
		resetStyles : function( element, styleList ) {
			var convertedKey;
			Object.keys( styleList ).forEach( function loop( key ) {
				if ( styleList[ key ] === null || styleList[ key ] === '' ) {
					convertedKey = key.replace( /[A-Z]/g, function regexFn( match ) {
						return '-' + match.toLowerCase();
					});
					element.style.removeProperty( convertedKey );
				} else {
					element.style[ key ] = styleList[ key ];
				}
			});
		},

		// Load content from specified url/anchor into an element (v4.13 - Sep 19)
		requestLoadInto : function( element, href, responseCallback ) {
			var instance = this,
				bodyRegex = /<body.*?>([\s\S]*)<\/body>/,
				id = href.match( instance.urlAnchorRegExp ),
				requestUrl = href,
				request = new XMLHttpRequest(),
				tempUrl;

			if ( id && id[ 0 ] ) {
				id = id[ 0 ];
				requestUrl = href.replace( id, '' );
				requestUrl = href.replace( ' ', '' );
			}
			// If there is a space in the url then split and use the later part as the selector
			if ( requestUrl.indexOf( ' ' ) !== -1 ) {
				tempUrl = requestUrl.split( ' ' );
				requestUrl = tempUrl[ 0 ];
				id = tempUrl[ 1 ];
			}

			// If the url supplied does not start with // or the protocol then add the current path origin
			if ( !requestUrl.match( /^((\/\/)|(http))/ ) ) {
				requestUrl = window.location.origin + requestUrl;
			}

			request.open( 'GET', requestUrl, true );
			request.onload = function onload() {
				var tempEl,
					elContent;

				if ( request.status >= 200 && request.status < 400 ) {
					tempEl = document.createElement( 'div' );
					tempEl.innerHTML = request.responseText;

					if ( id ) {
						elContent = tempEl.querySelector( id );
					}

					if ( elContent ) {
						element.innerHTML = elContent.outerHTML;
					} else if ( request.responseText.indexOf( '</body>' ) && request.responseText.indexOf( '</body>' ) > 0 ) {
						// If there was not valid ID given then make sure only the body of any page passed in is injected into the page
						element.innerHTML = bodyRegex.exec( request.responseText )[ 1 ];
					} else {
						element.innerHTML = request.responseText;
					}
				}
				if ( responseCallback ) {
					responseCallback( request.responseText, request.status );
				}
			};
			request.send();
		},

		// Find an element's parent for scrolling purposes (v4.13 - Sep 19)
		scrollParent : function( element, includeHidden ) {
			var style = getComputedStyle( element ),
				excludeStaticParent = style.position === 'absolute',
				overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/,
				parent;

			if ( style.position === 'fixed' ) {
				return document.body;
			}

			// eslint-disable-next-line no-cond-assign
			for ( parent = element; ( parent = parent.parentElement ); ) {
				style = getComputedStyle( parent );
				if ( excludeStaticParent && style.position === 'static' ) {
					// Do nothing
				} else if ( overflowRegex.test( style.overflow + style.overflowY + style.overflowX ) ) {
					return parent;
				} else {
					// Do nothing
				}
			}

			return document.body;
		},

		// Allow daisy-chaining to set multiple attributes in one go, eg setAttributes( el, { href : '#', class : 'whatever' } ); (v4.13 - Sep 19)
		setAttributes : function( element, attrs ) {
			Object.keys( attrs ).forEach( function loop( key ) {
				element.setAttribute( key, attrs[ key ] );
			});
		},

		// Find all sibling elements / sibling elements (with a particular selector if specified) (v4.13 - Sep 19)
		siblings : function( element, selector ) {
			var siblingsArr = [],
				instance = this;

			siblingsArr = Array.prototype.filter.call( element.parentNode.children, function loop( child ) {
				var isSibling = false;
				if ( child === element ) {
					// Do nothing
				} else if ( typeof selector === 'undefined' || instance.matches( child, selector ) ) {
					isSibling = true;
				} else {
					// Do nothing
				}
				return isSibling;
			});

			return siblingsArr;
		},

		/*
		 * Vanilla JS replacement for jQuery's slideUp / slideDown / slideToggle functions (v4.13 - Sep 19)
		 * eg: element.slideToggle( target element, animation duration (in ms), { options } );
		 */
		slideToggle : function( element, duration, options ) { // eslint-disable-line complexity
			var instance = this,
				timing = ( duration / 1000 ) + 's',
				toggleOptions = {
					variant : 'toggle', // ...down/up/toggle - default toggle
					overlapMargin : false, // ...boolean - default false
					customStartHeight : null, // ...number - default null
					customEndHeight : null, // ...number - default null
					display : 'block', // ...block/grid/flex - default block
					minFade : 0, // ...number - default 0
					maxFade : 1, // ...number - default 1
					fade : false, // ...boolean - default false
					callback : function() {
						// Do nothing
					},
				},
				overlap = 0, // Default if no overlap;
				nextStyles,
				startHeight,
				elStyles,
				startPaddingBottom,
				startPaddingTop,
				baseHeight,
				initialHeight;

			// Only run if animation is not progress
			if ( instance.isNode( element ) && !element.classList.contains( 'is-animating' ) ) {
				elStyles = window.getComputedStyle( element );
				startPaddingBottom = instance.removeMeasurementSuffix( elStyles.paddingBottom, true );
				startPaddingTop = instance.removeMeasurementSuffix( elStyles.paddingTop, true );

				// If options have been set update the default ones
				toggleOptions = instance.extend({}, toggleOptions, options );

				toggleOptions.savedStyles = {
					height : element.style.height,
					marginBottom : element.style.marginBottom,
					marginTop : element.style.marginTop,
					maxHeight : element.style.maxHeight,
					opacity : element.style.opacity,
					overflow : element.style.overflow,
					overflowX : element.style.overflowX,
					overflowY : element.style.overflowY,
					paddingBottom : element.style.paddingBottom,
					paddingTop : element.style.paddingTop,
					transition : element.style.transition,
				};

				/*
				 * To avoid jumping at start/end of transitions caused by overflow-y: hidden, get next sibling's top
				 * margin ready take that into consideration when calculating initial height
				 * Set parameter to 'overlapMargin' to true to use (false with 'overlap = 0' by default)
				 */
				if ( toggleOptions.overlapMargin && element.nextElementSibling ) {
					nextStyles = window.getComputedStyle( element.nextElementSibling );
					overlap = instance.removeMeasurementSuffix( nextStyles.marginTop, true );
				}

				// Get computed height of element plus any overlapping top margins from next sibling
				if ( toggleOptions.customStartHeight === null ) {
					startHeight = instance.addMeasurements( [ instance.removeMeasurementSuffix( element.offsetHeight ), overlap ] );
				} else {
					startHeight = toggleOptions.customStartHeight;
				}

				// If previously hidden, show element again before doing any animations
				if ( elStyles.display === 'none' ) {
					element.style.display = toggleOptions.display;
					element.style.paddingBottom = 0;
					element.style.paddingTop = 0;
				}

				/*
				 * Initial height without margins - needs setting specifically for setTimeout function as el.offsetHeight
				 * is modified in the 10ms before it runs and can return value including overlapping margins
				 * Set height to auto and get computed height of element less any overlapping top margins from next sibling
				 */
				element.style.height = 'auto';
				baseHeight = instance.removeMeasurementSuffix( elStyles.height );
				initialHeight = instance.subtractMeasurements( [ baseHeight, overlap ] );

				if ( toggleOptions.fade && ( toggleOptions.variant === 'down' || ( toggleOptions.variant === 'toggle' && startHeight === 0 ) ) ) {
					element.style.opacity = toggleOptions.minFade;
				} else {
					element.style.opacity = toggleOptions.maxFade;
				}

				// After calculations, set element height to start height, hide overflow for duration of transition, and add transition css
				element.style.height = instance.convertToPx( startHeight );
				element.style.maxHeight = instance.convertToPx( startHeight );
				element.style.overflow = 'hidden';
				element.style.overflowX = 'hidden';
				element.style.overflowY = 'hidden';
				element.style.transition = 'all ' + timing + ' ease-in-out';

				setTimeout( function timeoutFn() {
					if ( toggleOptions.variant === 'down' ) {
						element.classList.add( 'is-animating' );
						instance.slideDown( element, duration, toggleOptions, {
							initialHeight : initialHeight,
							startPaddingBottom : startPaddingBottom,
							startPaddingTop : startPaddingTop,
						});
					} else if ( toggleOptions.variant === 'up' ) {
						element.classList.add( 'is-animating' );
						instance.slideUp( element, duration, toggleOptions );
					} else if ( toggleOptions.variant === 'toggle' ) {
						element.classList.add( 'is-animating' );
						if ( startHeight === 0 ) {
							instance.slideDown( element, duration, toggleOptions, {
								initialHeight : initialHeight,
								startPaddingBottom : startPaddingBottom,
								startPaddingTop : startPaddingTop,
							});
						} else {
							instance.slideUp( element, duration, toggleOptions );
						}
					} else {
						// Error, as one of the above should apply, reset any styles
						instance.resetStyles( element, toggleOptions.savedStyles );
					}
				}, 20 );
			}
		},

		// Slide up function - slideToggle variant only (v4.13 - Sep 19)
		slideUp : function( element, duration, toggleOptions ) {
			var instance = this,
				endHeight = toggleOptions.customEndHeight || 0;

			if ( instance.isNode( element ) ) {
				element.style.maxHeight = instance.convertToPx( endHeight );
				element.style.height = instance.convertToPx( endHeight );
				element.style.marginBottom = 0;
				element.style.marginTop = 0;
				element.style.paddingBottom = 0;
				element.style.paddingTop = 0;
				if ( toggleOptions.fade ) {
					element.style.opacity = toggleOptions.minFade;
					toggleOptions.savedStyles.opacity = toggleOptions.minFade;
				}
				setTimeout( function timeoutFn() {
					var hide = false;
					if ( element.style.height === '0px' || element.style.height === 0 ) {
						hide = true;
					}
					element.classList.remove( 'is-animating' );
					if ( toggleOptions.savedStyles ) {
						instance.resetStyles( element, toggleOptions.savedStyles );
					}

					if ( hide ) {
						element.style.display = 'none';
						element.setAttribute( 'aria-hidden', 'true' );
					}
					toggleOptions.callback();
				}, duration );
			}
		},

		// Slide down function - slideToggle variant only (v4.13 - Sep 19)
		slideDown : function( element, duration, toggleOptions, options ) {
			var instance = this,
				endHeight = instance.addMeasurements( [ options.initialHeight, options.startPaddingTop, options.startPaddingBottom ] );

			if ( instance.isNode( element ) ) {
				if ( toggleOptions.customEndHeight !== null ) {
					endHeight = toggleOptions.customEndHeight;
				}
				if ( endHeight === 'auto' ) {
					element.style.maxHeight = '4000px';
				} else {
					element.style.maxHeight = instance.convertToPx( endHeight );
				}
				element.style.height = instance.convertToPx( endHeight );
				element.style.paddingBottom = instance.convertToPx( options.startPaddingBottom );
				element.style.paddingTop = instance.convertToPx( options.startPaddingTop );
				if ( toggleOptions.fade ) {
					element.style.opacity = toggleOptions.maxFade;
					toggleOptions.savedStyles.opacity = toggleOptions.maxFade;
				}
				setTimeout( function timeoutFn() {
					element.classList.remove( 'is-animating' );
					if ( toggleOptions.savedStyles ) {
						instance.resetStyles( element, toggleOptions.savedStyles );
					}
					element.style.display = toggleOptions.display;
					element.setAttribute( 'aria-hidden', 'false' );
					toggleOptions.callback();
				}, duration );
			}
		},

		// Get remainder from subtracted measurements in an array (v4.13 - Sep 19)
		subtractMeasurements : function( measurementArray ) {
			var instance = this,
				measurement = 0,
				i;

			for ( i = 0; i < measurementArray.length; i++ ) {
				if ( typeof measurementArray[ i ] !== 'number' ) {
					measurementArray[ i ] = instance.removeMeasurementSuffix( measurementArray[ i ] ); // Value is NaN, so attempt to convert before continuing
				}
				if ( i === 0 && typeof measurementArray[ i ] === 'number' ) {
					measurement = measurementArray[ i ];
				} else if ( typeof measurementArray[ i ] === 'number' ) {
					measurement -= measurementArray[ i ]; // Value is a number, so subtract it from current total
				} else {
					measurement = 'auto'; // Value is set to auto (so don't change it), or something else entirely (so set to auto rather than failing completely)...
					i = measurementArray.length; // ... then set count to be complete so no more subtractions are run
				}
			}

			return measurement;
		},

		// Unwrap child nodes from element and remove original element (v5.7 - Apr 21 - updated to remove text nodes)
		unwrap : function( element ) {
			var parent = element.parentNode;

			while ( element.firstChild ) {
				if ( element.firstChild.nodeType === Node.TEXT_NODE ) {
					element.firstChild.remove();
				} else {
					element.insertAdjacentElement( 'beforebegin', element.firstChild );
				}
			}
			parent.removeChild( element );
		},

		// Update browser history (v4.13 - Sep 19)
		updateHistory : function( anchor ) {
			var instance = this,
				currentUrl;

			if ( window.history.replaceState ) {
				/*
				 * Check that either there is no anchor on the url and that the anchor has no special characters before replacing
				 * This stops issues between some systems which use these for state management
				 */
				currentUrl = instance.getAnchor();
				if ( currentUrl !== null ) {
					window.history.replaceState( null, null, anchor );
				}
			}
		},

		// Wrap element with a new one (v4.13 - Sep 19)
		wrap : function( element, wrappingElement ) {
			var wrapper = wrappingElement || document.createElement( 'div' );

			element.parentNode.insertBefore( wrapper, element );
			return wrapper.appendChild( element );
		},

		// Wrap the children of an element within a single new child element (v4.13 - Sep 19)
		wrapInner : function( parent, wrapper, className ) {
			var instance = this,
				contentWrapper = wrapper;

			if ( typeof contentWrapper === 'string' ) {
				contentWrapper = document.createElement( wrapper );
			}

			if ( className ) {
				instance.addClass( contentWrapper, className );
			}

			parent.appendChild( contentWrapper );
			while ( parent.firstChild !== contentWrapper ) {
				contentWrapper.appendChild( parent.firstChild );
			}
		},
	};
});
